import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AMENITY_LABELS, Amenities, Field, FieldType, FIELD_TYPE_LABELS, TimeRangePrice } from '../../types';
import { createDefaultAmenities, createFieldWithAuth, districts, fields, updateFieldById } from '../../data/mockData';
import { getOwnerProfileById, getSession } from '../../data/authStore';

const SIZE_OPTIONS: Array<'5v5' | '6v6' | '7v7' | '8v8' | '9v9' | '10v10' | '11v11'> = [
  '5v5', '6v6', '7v7', '8v8', '9v9', '10v10', '11v11',
];
const DAY_OPTIONS = [
  { key: 'mon', label: 'Pazartesi' },
  { key: 'tue', label: 'Salı' },
  { key: 'wed', label: 'Çarşamba' },
  { key: 'thu', label: 'Perşembe' },
  { key: 'fri', label: 'Cuma' },
  { key: 'sat', label: 'Cumartesi' },
  { key: 'sun', label: 'Pazar' },
] as const;
const FULL_HOUR_OPTIONS = Array.from({ length: 25 }, (_, hour) => `${String(hour).padStart(2, '0')}:00`);

function toFullHour(value: string, fallback: string): string {
  if (!value || !value.includes(':')) return fallback;
  const [hourRaw] = value.split(':');
  const hour = Number(hourRaw);
  if (!Number.isInteger(hour) || hour < 0 || hour > 24) return fallback;
  return `${String(hour).padStart(2, '0')}:00`;
}

function parseDayRange(value: string): { enabled: boolean; start: string; end: string } {
  if (!value?.trim()) return { enabled: false, start: '09:00', end: '23:00' };
  const firstRange = value.split(',')[0].trim();
  const [start, end] = firstRange.split('-').map((item) => item.trim());
  if (!start || !end) return { enabled: false, start: '09:00', end: '23:00' };
  return { enabled: true, start: toFullHour(start, '09:00'), end: toFullHour(end, '23:00') };
}

function formatDayRange(enabled: boolean, start: string, end: string): string {
  if (!enabled) return '';
  return `${toFullHour(start, '09:00')}-${toFullHour(end, '23:00')}`;
}

function parseHourValue(value: string): number | null {
  if (!value || !value.includes(':')) return null;
  const [hStr, mStr] = value.split(':');
  const h = Number(hStr);
  const m = Number(mStr);
  if (!Number.isInteger(h) || !Number.isInteger(m) || h < 0 || h > 24 || m < 0 || m > 59) return null;
  if (h === 24 && m !== 0) return null;
  return h * 60 + m;
}

function isValidTimeRangeInput(raw: string): boolean {
  const parts = raw
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
  if (parts.length === 0) return false;

  return parts.every((part) => {
    const [startRaw, endRaw] = part.split('-').map((item) => item.trim());
    if (!startRaw || !endRaw) return false;
    const start = parseHourValue(startRaw);
    const end = parseHourValue(endRaw);
    if (start === null || end === null) return false;
    // same time invalid, overnight valid
    return start !== end;
  });
}

interface FieldForm {
  name: string;
  district: string;
  address: string;
  phone: string;
  pricePerHour: number;
  sizeOptions: Array<'5v5' | '6v6' | '7v7' | '8v8' | '9v9' | '10v10' | '11v11'>;
  turfType: string;
  slotPlanType: 'hourly';
  scheduleType: 'same-everyday' | 'by-day';
  slotIntervalMinutes: 60;
  availableTimeRanges: string;
  dayRanges: Record<'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat', string>;
  description: string;
  imageUrl: string;
  locationInputType: 'maps-link' | 'coordinates';
  mapsUrl: string;
  lat: number;
  lng: number;
  subscriptionPlan: 'free' | 'fan' | 'pro';
  amenities: Amenities;
  fieldType: FieldType;
  pricingByTimeRange: TimeRangePrice[];
  width: number;
  length: number;
  roofType: 'none' | 'open' | 'closed' | 'retractable';
  hasScoreboard: boolean;
  hasGoalNets: boolean;
  maxCapacity: number;
  waterPrice: number;
  shoeRentalPrice: number;
  isReservationPaused: boolean;
  reservationPausedMessage: string;
  paymentIban: string;
  paymentBankName: string;
  paymentAccountName: string;
  paymentNote: string;
  subscriptionEnabled: boolean;
  subscriptionPrice: number;
  subscriptionRules: string;
}

function createEmptyFieldForm(district: string): FieldForm {
  return {
    name: '',
    district,
    address: '',
    phone: '',
    pricePerHour: 250,
    sizeOptions: ['7v7'],
    turfType: 'Sentetik Çim',
    slotPlanType: 'hourly',
    scheduleType: 'same-everyday',
    slotIntervalMinutes: 60,
    availableTimeRanges: '09:00-12:00, 13:00-23:00',
    dayRanges: {
      mon: '09:00-23:00', tue: '09:00-23:00', wed: '09:00-23:00',
      thu: '09:00-23:00', fri: '09:00-23:00', sat: '10:00-23:00', sun: '10:00-23:00',
    },
    description: '',
    imageUrl: '',
    locationInputType: 'maps-link',
    mapsUrl: '',
    lat: 41.0082,
    lng: 28.9784,
    subscriptionPlan: 'free',
    amenities: createDefaultAmenities(),
    fieldType: 'indoor',
    pricingByTimeRange: [],
    width: 0,
    length: 0,
    roofType: 'closed',
    hasScoreboard: false,
    hasGoalNets: true,
    maxCapacity: 0,
    waterPrice: 0,
    shoeRentalPrice: 0,
    isReservationPaused: false,
    reservationPausedMessage: 'Şuanlık rezervasyon alımı durmuştur.',
    paymentIban: '',
    paymentBankName: '',
    paymentAccountName: '',
    paymentNote: '',
    subscriptionEnabled: false,
    subscriptionPrice: 0,
    subscriptionRules: '',
  };
}

type WizardStep = 'count' | 'details';

const AddFieldPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const session = getSession();
  const owner = session?.role === 'owner' ? getOwnerProfileById(session.ownerId) : undefined;
  const editFieldId = searchParams.get('edit');
  const editingField =
    session?.role === 'owner' && editFieldId
      ? fields.find((item) => item.id === editFieldId && item.ownerId === session.ownerId)
      : undefined;
  const isEditMode = Boolean(editFieldId && editingField);

  const [wizardStep, setWizardStep] = useState<WizardStep>('count');
  const [fieldCount, setFieldCount] = useState(1);
  const [forms, setForms] = useState<FieldForm[]>([]);
  const [activeFieldIdx, setActiveFieldIdx] = useState(0);
  const [error, setError] = useState('');
  const [successCount, setSuccessCount] = useState(0);

  useEffect(() => {
    if (!isEditMode || !editingField) return;
    const base = createEmptyFieldForm(editingField.district);
    const next: FieldForm = {
      ...base,
      name: editingField.name,
      district: editingField.district,
      address: editingField.address,
      phone: editingField.phone,
      pricePerHour: editingField.pricePerHour,
      sizeOptions: editingField.sizeOptions || [editingField.size],
      turfType: editingField.turfType,
      slotPlanType: 'hourly',
      scheduleType: editingField.availableTimeRangesByDay ? 'by-day' : 'same-everyday',
      slotIntervalMinutes: 60,
      availableTimeRanges: editingField.availableTimeRanges || base.availableTimeRanges,
      dayRanges: {
        ...base.dayRanges,
        ...(editingField.availableTimeRangesByDay || {}),
      },
      description: editingField.description || '',
      imageUrl: editingField.images?.[0] || '',
      locationInputType: editingField.locationInputType || 'maps-link',
      mapsUrl: editingField.mapsUrl || '',
      lat: editingField.lat,
      lng: editingField.lng,
      subscriptionPlan: editingField.subscriptionPlan || 'free',
      amenities: {
        ...createDefaultAmenities(),
        ...editingField.amenities,
      },
      fieldType: editingField.fieldType || 'indoor',
      pricingByTimeRange: editingField.pricingByTimeRange || [],
      width: editingField.width || 0,
      length: editingField.length || 0,
      roofType: editingField.roofType || 'none',
      hasScoreboard: Boolean(editingField.hasScoreboard),
      hasGoalNets: editingField.hasGoalNets !== false,
      maxCapacity: editingField.maxCapacity || 0,
      waterPrice: editingField.waterPrice || 0,
      shoeRentalPrice: editingField.shoeRentalPrice || 0,
      isReservationPaused: Boolean(editingField.isReservationPaused),
      reservationPausedMessage: editingField.reservationPausedMessage || 'Şuanlık rezervasyon alımı durmuştur.',
      paymentIban: editingField.paymentIban || '',
      paymentBankName: editingField.paymentBankName || '',
      paymentAccountName: editingField.paymentAccountName || '',
      paymentNote: editingField.paymentNote || '',
      subscriptionEnabled: Boolean(editingField.subscriptionEnabled),
      subscriptionPrice: editingField.subscriptionPrice || 0,
      subscriptionRules: editingField.subscriptionRules || '',
    };
    setForms([next]);
    setActiveFieldIdx(0);
    setWizardStep('details');
  }, [isEditMode, editingField]);

  const initializeForms = () => {
    const count = Math.max(1, Math.min(10, fieldCount));
    const defaultDistrict = districts[0] || '';
    const newForms: FieldForm[] = [];
    for (let i = 0; i < count; i++) {
      const form = createEmptyFieldForm(defaultDistrict);
      form.name = count > 1 ? `Saha ${i + 1}` : '';
      newForms.push(form);
    }
    setForms(newForms);
    setActiveFieldIdx(0);
    setWizardStep('details');
  };

  const updateForm = (idx: number, updates: Partial<FieldForm>) => {
    setForms(prev => prev.map((f, i) => i === idx ? { ...f, ...updates } : f));
  };

  const handleCoverImageUpload = (idx: number, fileList: FileList | null) => {
    const file = fileList?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Lütfen geçerli bir görsel dosyası seçin.');
      return;
    }
    const maxSizeBytes = 6 * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      setError('Kapak fotoğrafı en fazla 6 MB olabilir.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === 'string' ? reader.result : '';
      updateForm(idx, { imageUrl: dataUrl });
      setError('');
    };
    reader.onerror = () => {
      setError('Fotoğraf okunamadı. Lütfen tekrar deneyin.');
    };
    reader.readAsDataURL(file);
  };

  const setAmenity = (idx: number, key: keyof Amenities, value: boolean) => {
    setForms(prev => prev.map((f, i) => i === idx ? {
      ...f,
      amenities: { ...f.amenities, [key]: value },
    } : f));
  };

  const addTimeRangePrice = (idx: number) => {
    setForms(prev => prev.map((f, i) => {
      if (i !== idx) return f;
      return {
        ...f,
        pricingByTimeRange: [...f.pricingByTimeRange, { startHour: 18, endHour: 24, price: f.pricePerHour + 100, label: 'Akşam Tarifesi' }],
      };
    }));
  };

  const removeTimeRangePrice = (fieldIdx: number, rangeIdx: number) => {
    setForms(prev => prev.map((f, i) => {
      if (i !== fieldIdx) return f;
      return {
        ...f,
        pricingByTimeRange: f.pricingByTimeRange.filter((_, ri) => ri !== rangeIdx),
      };
    }));
  };

  const updateTimeRangePrice = (fieldIdx: number, rangeIdx: number, updates: Partial<TimeRangePrice>) => {
    setForms(prev => prev.map((f, i) => {
      if (i !== fieldIdx) return f;
      return {
        ...f,
        pricingByTimeRange: f.pricingByTimeRange.map((r, ri) => ri === rangeIdx ? { ...r, ...updates } : r),
      };
    }));
  };

  const handleSubmitAll = (e: React.FormEvent) => {
    e.preventDefault();
    if (!owner || owner.status !== 'approved') return;

    let created = 0;
    for (let i = 0; i < forms.length; i++) {
      const form = forms[i];
      if (!form.name.trim() || !form.address.trim() || !form.phone.trim()) {
        setError(`Saha ${i + 1}: Lütfen saha adı, adres ve telefon alanlarını doldurun.`);
        setActiveFieldIdx(i);
        return;
      }
      if (!form.paymentIban.trim() || !form.paymentBankName.trim() || !form.paymentAccountName.trim()) {
        setError(`Saha ${i + 1}: Lütfen ödeme için IBAN, banka adı ve hesap sahibini girin.`);
        setActiveFieldIdx(i);
        return;
      }
      if (form.subscriptionEnabled && (!form.subscriptionPrice || form.subscriptionPrice <= 0)) {
        setError(`Saha ${i + 1}: Abonelik açıkken abonelik ücreti 0'dan büyük olmalıdır.`);
        setActiveFieldIdx(i);
        return;
      }

      const rangesByDay =
        form.scheduleType === 'by-day'
          ? Object.fromEntries(
              DAY_OPTIONS.map(({ key }) => [key, form.dayRanges[key as keyof typeof form.dayRanges].trim()])
                .filter(([, value]) => Boolean(value))
            )
          : undefined;
      const hasAnyDayRange = rangesByDay && Object.keys(rangesByDay).length > 0;

      if (form.scheduleType === 'same-everyday' && !form.availableTimeRanges.trim()) {
        setError(`Saha ${i + 1}: Lütfen müsait saat aralıklarını girin.`);
        setActiveFieldIdx(i);
        return;
      }
      if (form.scheduleType === 'same-everyday' && !isValidTimeRangeInput(form.availableTimeRanges.trim())) {
        setError(`Saha ${i + 1}: Saat aralığı formatı geçersiz. Örn: 09:00-12:00, 22:00-03:00`);
        setActiveFieldIdx(i);
        return;
      }
      if (form.scheduleType === 'by-day' && !hasAnyDayRange) {
        setError(`Saha ${i + 1}: Günlere göre planda en az bir güne saat aralığı girmelisiniz.`);
        setActiveFieldIdx(i);
        return;
      }
      if (form.scheduleType === 'by-day' && rangesByDay) {
        const invalidDay = Object.entries(rangesByDay).find(([, range]) => {
          return !isValidTimeRangeInput(String(range));
        });
        if (invalidDay) {
          const dayLabel = DAY_OPTIONS.find((day) => day.key === invalidDay[0])?.label || invalidDay[0];
          setError(`Saha ${i + 1}: ${dayLabel} günü için saat aralığı geçersiz. Örn: 09:00-23:00 veya 22:00-03:00`);
          setActiveFieldIdx(i);
          return;
        }
      }

      const slotIntervalMinutes: 60 = 60;
      const primarySize =
        form.sizeOptions.find((s) => s === '5v5' || s === '7v7' || s === '11v11') || '7v7';

      const payload = {
        sizeOptions: form.sizeOptions,
        name: form.name.trim(),
        district: form.district,
        address: form.address.trim(),
        phone: form.phone.trim(),
        pricePerHour: Number(form.pricePerHour),
        size: primarySize as Field['size'],
        turfType: form.turfType.trim() || 'Sentetik Çim',
        openingHour: 0,
        closingHour: 24,
        slotIntervalMinutes,
        availableTimeRanges: form.scheduleType === 'same-everyday' ? form.availableTimeRanges.trim() : undefined,
        availableTimeRangesByDay: form.scheduleType === 'by-day' ? (rangesByDay as any) : undefined,
        ownerId: owner.id,
        description: form.description.trim(),
        images: form.imageUrl.trim() ? [form.imageUrl.trim()] : [],
        locationInputType: form.locationInputType,
        mapsUrl: form.mapsUrl.trim() || undefined,
        lat: form.locationInputType === 'coordinates' ? Number(form.lat) : undefined,
        lng: form.locationInputType === 'coordinates' ? Number(form.lng) : undefined,
        subscriptionPlan: form.subscriptionPlan,
        amenities: form.amenities,
        fieldType: form.fieldType,
        pricingByTimeRange: form.pricingByTimeRange.length > 0 ? form.pricingByTimeRange : undefined,
        isReservationPaused: form.isReservationPaused,
        reservationPausedMessage: form.reservationPausedMessage,
        width: form.width || undefined,
        length: form.length || undefined,
        roofType: form.roofType,
        hasScoreboard: form.hasScoreboard,
        hasGoalNets: form.hasGoalNets,
        maxCapacity: form.maxCapacity || undefined,
        waterPrice: form.waterPrice || undefined,
        shoeRentalPrice: form.shoeRentalPrice || undefined,
        paymentIban: form.paymentIban.trim(),
        paymentBankName: form.paymentBankName.trim(),
        paymentAccountName: form.paymentAccountName.trim(),
        paymentNote: form.paymentNote.trim() || undefined,
        subscriptionEnabled: form.subscriptionEnabled,
        subscriptionPrice: form.subscriptionEnabled ? Number(form.subscriptionPrice) : 0,
        subscriptionRules: form.subscriptionRules.trim() || undefined,
      };

      if (isEditMode && editingField && i === 0) {
        updateFieldById(editingField.id, payload);
        created++;
        continue;
      }

      const result = createFieldWithAuth(payload);

      if (!result.ok) {
        setError(`Saha ${i + 1}: ${result.error}`);
        setActiveFieldIdx(i);
        return;
      }
      created++;
    }

    setSuccessCount(created);
  };

  if (successCount > 0) {
    return (
      <div className="bg-slate-50 min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {isEditMode ? 'Saha Profili Güncellendi!' : `${successCount} Saha Başarıyla Eklendi!`}
          </h2>
          <p className="text-gray-500 mb-6">
            {isEditMode ? 'Saha bilgileri kaydedildi. Panelden yönetmeye devam edebilirsiniz.' : 'Sahalarınız sisteme kaydedildi. Panel üzerinden yönetebilirsiniz.'}
          </p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => navigate('/panel')} className="px-5 py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors">
              Panele Git
            </button>
            <button onClick={() => navigate('/sahalar')} className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors">
              Sahaları Gör
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {(!session || session.role !== 'owner') && (
          <div className="bg-white rounded-xl border border-amber-200 shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Saha eklemek için sahip profili gerekli</h2>
            <p className="text-sm text-gray-600 mb-4">Bu sayfaya erişmek için önce halısaha sahibi profili açmalı ve giriş yapmalısınız.</p>
            <Link to="/giris" className="inline-flex items-center rounded-lg bg-primary-600 text-white px-4 py-2 text-sm font-medium">Giriş Yap / Hesap Aç</Link>
          </div>
        )}
        {session?.role === 'owner' && owner?.status === 'pending' && (
          <div className="bg-white rounded-xl border border-orange-200 shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Admin onayı bekleniyor</h2>
            <p className="text-sm text-gray-600">Profiliniz onaylandıktan sonra saha ekleyebilirsiniz.</p>
          </div>
        )}
        {session?.role === 'owner' && !owner && (
          <div className="bg-white rounded-xl border border-rose-200 shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Profil bulunamadı</h2>
            <p className="text-sm text-gray-600">Oturum bilgisi geçersiz. Lütfen tekrar giriş yapın.</p>
            <Link to="/giris" className="inline-flex mt-3 items-center rounded-lg bg-primary-600 text-white px-4 py-2 text-sm font-medium">Girişe Dön</Link>
          </div>
        )}

        {session?.role === 'owner' && owner?.status === 'approved' && (
          <>
            {wizardStep === 'count' && !isEditMode && (
              <div className="max-w-lg mx-auto">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">Sahalarınızı Ekleyin</h1>
                  <p className="text-gray-500">Kaç tane halı sahanız var? Her biri için ayrı profil oluşturulacak.</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">Saha Sayısı</label>
                  <div className="flex items-center gap-4 mb-6">
                    <button type="button" onClick={() => setFieldCount(Math.max(1, fieldCount - 1))}
                      className="w-10 h-10 rounded-lg border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-50">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" /></svg>
                    </button>
                    <div className="flex-1 text-center">
                      <span className="text-5xl font-bold text-primary-600">{fieldCount}</span>
                      <p className="text-gray-400 text-sm mt-1">saha</p>
                    </div>
                    <button type="button" onClick={() => setFieldCount(Math.min(10, fieldCount + 1))}
                      className="w-10 h-10 rounded-lg border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-50">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {[1, 2, 3, 4, 5].map(n => (
                      <button key={n} type="button" onClick={() => setFieldCount(n)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${fieldCount === n ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400'}`}>
                        {n} Saha
                      </button>
                    ))}
                  </div>
                  <button type="button" onClick={initializeForms}
                    className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 rounded-lg transition-colors">
                    Devam Et
                  </button>
                </div>
              </div>
            )}

            {wizardStep === 'details' && forms.length > 0 && (
              <>
                <div className="mb-6">
                  {!isEditMode && (
                    <button type="button" onClick={() => setWizardStep('count')}
                      className="text-sm text-gray-500 hover:text-primary-600 flex items-center gap-1 mb-4">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                      Geri Dön
                    </button>
                  )}
                  <h1 className="text-3xl font-bold text-gray-900">{isEditMode ? 'Saha Profili Düzenle' : 'Saha Detayları'}</h1>
                  <p className="text-gray-500 mt-1">
                    {isEditMode ? 'Sahanızın tüm özelliklerini bu panelden güncelleyebilirsiniz.' : `${forms.length} saha için bilgileri doldurun.`}
                  </p>
                </div>

                {forms.length > 1 && (
                  <div className="flex space-x-2 overflow-x-auto pb-3 mb-6">
                    {forms.map((f, i) => (
                      <button key={i} type="button" onClick={() => setActiveFieldIdx(i)}
                        className={`flex-shrink-0 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors border ${activeFieldIdx === i ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400'}`}>
                        {f.name || `Saha ${i + 1}`}
                      </button>
                    ))}
                  </div>
                )}

                <form onSubmit={handleSubmitAll} className="space-y-6">
                  {(() => {
                    const form = forms[activeFieldIdx];
                    const idx = activeFieldIdx;
                    if (!form) return null;
                    const timeOptions = FULL_HOUR_OPTIONS;
                    return (
                      <>
                        {/* Temel Bilgiler */}
                        <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                          <h2 className="text-lg font-semibold text-gray-900 mb-4">Temel Bilgiler</h2>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm text-gray-600 mb-2">Saha Adı *</label>
                              <input type="text" value={form.name} onChange={(e) => updateForm(idx, { name: e.target.value })}
                                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 focus:outline-none focus:border-primary-500"
                                placeholder="Örn: Kartal Merkez Halı Saha" />
                            </div>
                            <div>
                              <label className="block text-sm text-gray-600 mb-2">İlçe</label>
                              <select value={form.district} onChange={(e) => updateForm(idx, { district: e.target.value })}
                                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 focus:outline-none focus:border-primary-500">
                                {districts.map((d) => (<option key={d} value={d}>İstanbul / {d}</option>))}
                              </select>
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-sm text-gray-600 mb-2">Adres *</label>
                              <input type="text" value={form.address} onChange={(e) => updateForm(idx, { address: e.target.value })}
                                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 focus:outline-none focus:border-primary-500"
                                placeholder="Mahalle, cadde, kapı no" />
                            </div>
                            <div>
                              <label className="block text-sm text-gray-600 mb-2">Telefon *</label>
                              <input type="text" value={form.phone} onChange={(e) => updateForm(idx, { phone: e.target.value })}
                                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 focus:outline-none focus:border-primary-500"
                                placeholder="0(5xx) xxx xx xx" />
                            </div>
                            <div>
                              <label className="block text-sm text-gray-600 mb-2">Kapak Fotoğrafı</label>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleCoverImageUpload(idx, e.target.files)}
                                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 file:mr-3 file:rounded-md file:border-0 file:bg-primary-600 file:px-3 file:py-1.5 file:text-white file:text-sm"
                              />
                              <p className="text-xs text-gray-500 mt-1">JPG, PNG, WEBP (maks. 6 MB)</p>
                              {form.imageUrl && (
                                <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-2">
                                  <img src={form.imageUrl} alt="Kapak önizleme" className="w-full h-28 object-cover rounded-md border border-gray-200" />
                                  <button
                                    type="button"
                                    onClick={() => updateForm(idx, { imageUrl: '' })}
                                    className="mt-2 text-xs text-rose-700 border border-rose-200 bg-white rounded px-2 py-1"
                                  >
                                    Fotoğrafı Kaldır
                                  </button>
                                </div>
                              )}
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-sm text-gray-600 mb-2">Kısa Açıklama</label>
                              <textarea value={form.description} onChange={(e) => updateForm(idx, { description: e.target.value })} rows={2}
                                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 focus:outline-none focus:border-primary-500 resize-none"
                                placeholder="Sahaya dair kısa tanıtım metni" />
                            </div>
                          </div>
                        </section>

                        {/* Saha Tipi */}
                        <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                          <h2 className="text-lg font-semibold text-gray-900 mb-4">Saha Tipi & Fiziksel Özellikler</h2>
                          <div className="mb-4">
                            <label className="block text-sm text-gray-600 mb-2">Saha Türü</label>
                            <div className="grid grid-cols-3 gap-3">
                              {(Object.keys(FIELD_TYPE_LABELS) as FieldType[]).map(type => {
                                const info = FIELD_TYPE_LABELS[type];
                                return (
                                  <button key={type} type="button" onClick={() => updateForm(idx, { fieldType: type })}
                                    className={`p-4 rounded-xl border-2 text-center transition-all ${form.fieldType === type ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                                    <span className="text-2xl block mb-1">{info.icon}</span>
                                    <span className="text-sm font-semibold text-gray-900 block">{info.label}</span>
                                    <span className="text-xs text-gray-500">{info.description}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <label className="block text-sm text-gray-600 mb-2">Çatı Tipi</label>
                              <select value={form.roofType} onChange={(e) => updateForm(idx, { roofType: e.target.value as any })}
                                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900">
                                <option value="none">Çatı Yok</option>
                                <option value="open">Açık Çatı / Tente</option>
                                <option value="closed">Kapalı Çatı</option>
                                <option value="retractable">Açılır-Kapanır</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm text-gray-600 mb-2">Genişlik (m)</label>
                              <input type="number" min={0} value={form.width || ''} onChange={(e) => updateForm(idx, { width: Number(e.target.value) })}
                                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900" placeholder="28" />
                            </div>
                            <div>
                              <label className="block text-sm text-gray-600 mb-2">Uzunluk (m)</label>
                              <input type="number" min={0} value={form.length || ''} onChange={(e) => updateForm(idx, { length: Number(e.target.value) })}
                                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900" placeholder="45" />
                            </div>
                            <div>
                              <label className="block text-sm text-gray-600 mb-2">Kapasite (kişi)</label>
                              <input type="number" min={0} value={form.maxCapacity || ''} onChange={(e) => updateForm(idx, { maxCapacity: Number(e.target.value) })}
                                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900" placeholder="100" />
                            </div>
                            <div>
                              <label className="block text-sm text-gray-600 mb-2">Su Ücreti (₺)</label>
                              <input type="number" min={0} value={form.waterPrice || ''} onChange={(e) => updateForm(idx, { waterPrice: Number(e.target.value) })}
                                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900" placeholder="20" />
                            </div>
                            <div>
                              <label className="block text-sm text-gray-600 mb-2">Krampon Kiralama (₺)</label>
                              <input type="number" min={0} value={form.shoeRentalPrice || ''} onChange={(e) => updateForm(idx, { shoeRentalPrice: Number(e.target.value) })}
                                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900" placeholder="100" />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4 mt-4">
                            <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-3">
                              <span className="text-sm text-gray-700">Skor Tablası</span>
                              <div className="flex gap-2">
                                <button type="button" onClick={() => updateForm(idx, { hasScoreboard: true })}
                                  className={`px-3 py-1 rounded text-sm font-medium border ${form.hasScoreboard ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-700 border-gray-300'}`}>Evet</button>
                                <button type="button" onClick={() => updateForm(idx, { hasScoreboard: false })}
                                  className={`px-3 py-1 rounded text-sm font-medium border ${!form.hasScoreboard ? 'bg-rose-600 text-white border-rose-600' : 'bg-white text-gray-700 border-gray-300'}`}>Hayır</button>
                              </div>
                            </div>
                            <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-3">
                              <span className="text-sm text-gray-700">Kale Fileleri</span>
                              <div className="flex gap-2">
                                <button type="button" onClick={() => updateForm(idx, { hasGoalNets: true })}
                                  className={`px-3 py-1 rounded text-sm font-medium border ${form.hasGoalNets ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-700 border-gray-300'}`}>Evet</button>
                                <button type="button" onClick={() => updateForm(idx, { hasGoalNets: false })}
                                  className={`px-3 py-1 rounded text-sm font-medium border ${!form.hasGoalNets ? 'bg-rose-600 text-white border-rose-600' : 'bg-white text-gray-700 border-gray-300'}`}>Hayır</button>
                              </div>
                            </div>
                          </div>
                        </section>

                        {/* Fiyatlandırma */}
                        <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                          <h2 className="text-lg font-semibold text-gray-900 mb-4">Fiyatlandırma</h2>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <div>
                              <label className="block text-sm text-gray-600 mb-2">Saatlik Ücret (₺) - Varsayılan</label>
                              <input type="number" min={0} step={10} value={form.pricePerHour}
                                onChange={(e) => updateForm(idx, { pricePerHour: Number(e.target.value) })}
                                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 focus:outline-none focus:border-primary-500" />
                              <p className="text-xs text-gray-500 mt-1">Bu fiyat, özel tarife belirlenmemiş saatler için geçerlidir.</p>
                            </div>
                            <div>
                              <label className="block text-sm text-gray-600 mb-2">Rezervasyon Başlangıcı</label>
                              <div className="px-3 py-2 rounded-lg border border-gray-300 bg-gray-50 text-sm text-gray-700">
                                Sadece tam saatlerde (09:00, 10:00, 11:00 ...)
                              </div>
                              <p className="text-xs text-gray-500 mt-1">Seanslar her zaman 60 dakikadır.</p>
                            </div>
                          </div>
                          <div className="border-t border-gray-200 pt-4">
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <h3 className="text-sm font-semibold text-gray-900">Saat Bazlı Fiyatlandırma</h3>
                                <p className="text-xs text-gray-500">Akşam saatleri gibi özel saat aralıkları için farklı fiyat belirleyin.</p>
                              </div>
                              <button type="button" onClick={() => addTimeRangePrice(idx)}
                                className="flex items-center gap-1 px-3 py-1.5 bg-primary-50 text-primary-700 rounded-lg text-sm font-medium hover:bg-primary-100 transition-colors">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                Tarife Ekle
                              </button>
                            </div>
                            {form.pricingByTimeRange.length > 0 ? (
                              <div className="space-y-3">
                                {form.pricingByTimeRange.map((range, ri) => (
                                  <div key={ri} className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-3">
                                      <div className="flex items-center gap-2">
                                        <span className="text-amber-600">&#9201;</span>
                                        <input type="text" value={range.label || ''} onChange={(e) => updateTimeRangePrice(idx, ri, { label: e.target.value })}
                                          className="bg-white border border-amber-200 rounded px-2 py-1 text-sm text-gray-900" placeholder="Tarife adı" />
                                      </div>
                                      <button type="button" onClick={() => removeTimeRangePrice(idx, ri)} className="text-rose-500 hover:text-rose-700">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                      </button>
                                    </div>
                                    <div className="grid grid-cols-3 gap-3">
                                      <div>
                                        <label className="block text-xs text-amber-700 mb-1">Başlangıç</label>
                                        <select value={range.startHour} onChange={(e) => updateTimeRangePrice(idx, ri, { startHour: Number(e.target.value) })}
                                          className="w-full bg-white border border-amber-200 rounded px-2 py-1.5 text-sm">
                                          {Array.from({ length: 24 }, (_, h) => (<option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>))}
                                        </select>
                                      </div>
                                      <div>
                                        <label className="block text-xs text-amber-700 mb-1">Bitiş</label>
                                        <select value={range.endHour} onChange={(e) => updateTimeRangePrice(idx, ri, { endHour: Number(e.target.value) })}
                                          className="w-full bg-white border border-amber-200 rounded px-2 py-1.5 text-sm">
                                          {Array.from({ length: 24 }, (_, h) => (<option key={h + 1} value={h + 1}>{String(h + 1).padStart(2, '0')}:00</option>))}
                                        </select>
                                      </div>
                                      <div>
                                        <label className="block text-xs text-amber-700 mb-1">Fiyat (₺)</label>
                                        <input type="number" min={0} step={10} value={range.price}
                                          onChange={(e) => updateTimeRangePrice(idx, ri, { price: Number(e.target.value) })}
                                          className="w-full bg-white border border-amber-200 rounded px-2 py-1.5 text-sm" />
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                                <p className="text-gray-400 text-sm">Özel tarife yok. Tüm saatler varsayılan fiyattan uygulanır.</p>
                              </div>
                            )}
                          </div>
                        </section>

                        {/* Rezervasyon Durumu */}
                        <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                          <h2 className="text-lg font-semibold text-gray-900 mb-4">Rezervasyon Durumu</h2>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                            <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                              <div>
                                <p className="text-sm font-medium text-gray-800">Rezervasyon Alımı</p>
                                <p className="text-xs text-gray-500 mt-1">İsterseniz geçici olarak rezervasyonları kapatabilirsiniz.</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => updateForm(idx, { isReservationPaused: !form.isReservationPaused })}
                                className={`px-3 py-1.5 rounded-md text-sm font-medium border ${form.isReservationPaused ? 'bg-rose-600 text-white border-rose-600' : 'bg-emerald-600 text-white border-emerald-600'}`}
                              >
                                {form.isReservationPaused ? 'Kapalı' : 'Açık'}
                              </button>
                            </div>
                            <div>
                              <label className="block text-sm text-gray-600 mb-2">Kapalıysa Gösterilecek Mesaj</label>
                              <input
                                type="text"
                                value={form.reservationPausedMessage}
                                onChange={(e) => updateForm(idx, { reservationPausedMessage: e.target.value })}
                                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900"
                                placeholder="Şuanlık rezervasyon alımı durmuştur."
                              />
                            </div>
                          </div>
                        </section>

                        {/* Oyun & Takvim */}
                        <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                          <h2 className="text-lg font-semibold text-gray-900 mb-4">Oyun Bilgileri & Takvim</h2>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                              <label className="block text-sm text-gray-600 mb-2">Formatlar</label>
                              <div className="flex flex-wrap gap-2">
                                {SIZE_OPTIONS.map((option) => {
                                  const selected = form.sizeOptions.includes(option);
                                  return (
                                    <button key={option} type="button"
                                      onClick={() => {
                                        const has = form.sizeOptions.includes(option);
                                        const next = has ? form.sizeOptions.filter((item) => item !== option) : [...form.sizeOptions, option];
                                        updateForm(idx, { sizeOptions: next.length > 0 ? next : ['7v7' as const] });
                                      }}
                                      className={`px-3 py-1.5 rounded-md text-sm border ${selected ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-700 border-gray-300'}`}>
                                      {option}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm text-gray-600 mb-2">Zemin Tipi</label>
                              <input type="text" value={form.turfType} onChange={(e) => updateForm(idx, { turfType: e.target.value })}
                                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900" placeholder="Sentetik Çim / Doğal Çim" />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm text-gray-600 mb-2">Saat Planı</label>
                              <select value={form.scheduleType} onChange={(e) => updateForm(idx, { scheduleType: e.target.value as any })}
                                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900">
                                <option value="same-everyday">Her gün aynı</option>
                                <option value="by-day">Günlere göre</option>
                              </select>
                            </div>
                            <div>
                              {form.scheduleType === 'same-everyday' ? (
                                <>
                                  <label className="block text-sm text-gray-600 mb-2">Müsait Saat Aralıkları</label>
                                  <input type="text" value={form.availableTimeRanges}
                                    onChange={(e) => updateForm(idx, { availableTimeRanges: e.target.value })}
                                    className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900"
                                    placeholder="09:00-12:00, 13:00-23:00, 22:00-03:00" />
                                </>
                              ) : (
                                <div className="space-y-2">
                                  <label className="block text-sm text-gray-600 mb-2">Gün Planı</label>
                                  <p className="text-xs text-gray-500 mb-3">Açık/kapalı günleri ve saatleri buradan hızlıca seçin.</p>
                                  <div className="flex flex-wrap gap-2 mb-3">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const next = { ...form.dayRanges };
                                        DAY_OPTIONS.forEach((day) => {
                                          next[day.key] = day.key === 'sat' || day.key === 'sun' ? '10:00-23:00' : '09:00-23:00';
                                        });
                                        updateForm(idx, { dayRanges: next });
                                      }}
                                      className="px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-xs text-gray-700"
                                    >
                                      Önerilen Planı Uygula
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const next = { ...form.dayRanges };
                                        DAY_OPTIONS.forEach((day) => {
                                          next[day.key] = '';
                                        });
                                        updateForm(idx, { dayRanges: next });
                                      }}
                                      className="px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-xs text-gray-700"
                                    >
                                      Tüm Günleri Kapat
                                    </button>
                                  </div>
                                  <div className="space-y-2">
                                    {DAY_OPTIONS.map((day) => {
                                      const parsed = parseDayRange(form.dayRanges[day.key]);
                                      return (
                                        <div key={day.key} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center bg-gray-50 border border-gray-200 rounded-lg p-2">
                                          <div className="md:col-span-3 text-xs font-medium text-gray-700">{day.label}</div>
                                          <div className="md:col-span-2">
                                            <button
                                              type="button"
                                              onClick={() =>
                                                updateForm(idx, {
                                                  dayRanges: {
                                                    ...form.dayRanges,
                                                    [day.key]: formatDayRange(!parsed.enabled, parsed.start, parsed.end),
                                                  },
                                                })
                                              }
                                              className={`w-full text-xs rounded-md px-2 py-1.5 border ${parsed.enabled ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-gray-200 text-gray-700 border-gray-300'}`}
                                            >
                                              {parsed.enabled ? 'Açık' : 'Kapalı'}
                                            </button>
                                          </div>
                                          <div className="md:col-span-3">
                                            <select
                                              disabled={!parsed.enabled}
                                              value={parsed.start}
                                              onChange={(e) =>
                                                updateForm(idx, {
                                                  dayRanges: {
                                                    ...form.dayRanges,
                                                    [day.key]: formatDayRange(parsed.enabled, e.target.value, parsed.end),
                                                  },
                                                })
                                              }
                                              className="w-full bg-white border border-gray-300 rounded px-2 py-1.5 text-xs disabled:opacity-50"
                                            >
                                              {timeOptions.map((option) => (
                                                <option key={`${day.key}-start-${option}`} value={option}>{option}</option>
                                              ))}
                                            </select>
                                          </div>
                                          <div className="md:col-span-1 text-center text-xs text-gray-500">-</div>
                                          <div className="md:col-span-3">
                                            <select
                                              disabled={!parsed.enabled}
                                              value={parsed.end}
                                              onChange={(e) =>
                                                updateForm(idx, {
                                                  dayRanges: {
                                                    ...form.dayRanges,
                                                    [day.key]: formatDayRange(parsed.enabled, parsed.start, e.target.value),
                                                  },
                                                })
                                              }
                                              className="w-full bg-white border border-gray-300 rounded px-2 py-1.5 text-xs disabled:opacity-50"
                                            >
                                              {timeOptions.map((option) => (
                                                <option key={`${day.key}-end-${option}`} value={option}>{option}</option>
                                              ))}
                                            </select>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </section>

                        {/* Konum */}
                        <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                          <h2 className="text-lg font-semibold text-gray-900 mb-4">Konum Bilgisi</h2>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="md:col-span-2 flex gap-2">
                              <button type="button" onClick={() => updateForm(idx, { locationInputType: 'maps-link' })}
                                className={`px-4 py-2 rounded-lg border ${form.locationInputType === 'maps-link' ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-700 border-gray-300'}`}>Google Maps Linki</button>
                              <button type="button" onClick={() => updateForm(idx, { locationInputType: 'coordinates' })}
                                className={`px-4 py-2 rounded-lg border ${form.locationInputType === 'coordinates' ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-700 border-gray-300'}`}>Koordinat Seç</button>
                            </div>
                            {form.locationInputType === 'maps-link' ? (
                              <div className="md:col-span-2">
                                <input type="url" value={form.mapsUrl} onChange={(e) => updateForm(idx, { mapsUrl: e.target.value })}
                                  className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900" placeholder="https://maps.google.com/..." />
                              </div>
                            ) : (
                              <>
                                <div>
                                  <label className="block text-sm text-gray-600 mb-2">Enlem</label>
                                  <input type="number" step="0.0001" value={form.lat} onChange={(e) => updateForm(idx, { lat: Number(e.target.value) })}
                                    className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900" />
                                </div>
                                <div>
                                  <label className="block text-sm text-gray-600 mb-2">Boylam</label>
                                  <input type="number" step="0.0001" value={form.lng} onChange={(e) => updateForm(idx, { lng: Number(e.target.value) })}
                                    className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900" />
                                </div>
                              </>
                            )}
                          </div>
                        </section>

                        {/* Ödeme Bilgisi */}
                        <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                          <h2 className="text-lg font-semibold text-gray-900 mb-4">Ödeme Bilgisi (Havale/EFT)</h2>
                          <p className="text-sm text-gray-500 mb-4">
                            Kullanıcılar rezervasyonda bu bilgileri görecek. IBAN, banka adı ve hesap sahibi zorunludur.
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="md:col-span-2">
                              <label className="block text-sm text-gray-600 mb-2">IBAN *</label>
                              <input
                                type="text"
                                value={form.paymentIban}
                                onChange={(e) => updateForm(idx, { paymentIban: e.target.value })}
                                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900"
                                placeholder="TR00 0000 0000 0000 0000 0000 00"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-gray-600 mb-2">Banka Adı *</label>
                              <input
                                type="text"
                                value={form.paymentBankName}
                                onChange={(e) => updateForm(idx, { paymentBankName: e.target.value })}
                                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900"
                                placeholder="Örn: Ziraat Bankası"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-gray-600 mb-2">Hesap Sahibi *</label>
                              <input
                                type="text"
                                value={form.paymentAccountName}
                                onChange={(e) => updateForm(idx, { paymentAccountName: e.target.value })}
                                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900"
                                placeholder="Ad Soyad / Firma Ünvanı"
                              />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-sm text-gray-600 mb-2">Saha Sahibinin Notu</label>
                              <textarea
                                rows={3}
                                value={form.paymentNote}
                                onChange={(e) => updateForm(idx, { paymentNote: e.target.value })}
                                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 resize-none"
                                placeholder="Örn: Açıklamaya takım adı yazın, dekontu WhatsApp'tan paylaşın."
                              />
                            </div>
                          </div>
                        </section>

                        {/* Abonelik Ayarları */}
                        <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                          <h2 className="text-lg font-semibold text-gray-900 mb-4">Abonelik Ayarları</h2>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                              <div>
                                <p className="text-sm font-medium text-gray-800">Abonelik Aktif</p>
                                <p className="text-xs text-gray-500 mt-1">Açık olursa kullanıcı slotu iptale kadar rezerve edilir.</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => updateForm(idx, { subscriptionEnabled: !form.subscriptionEnabled })}
                                className={`px-3 py-1.5 rounded-md text-sm font-medium border ${form.subscriptionEnabled ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-gray-200 text-gray-700 border-gray-300'}`}
                              >
                                {form.subscriptionEnabled ? 'Açık' : 'Kapalı'}
                              </button>
                            </div>
                            <div>
                              <label className="block text-sm text-gray-600 mb-2">Abonelik Ücreti (₺)</label>
                              <input
                                type="number"
                                min={0}
                                step={10}
                                value={form.subscriptionPrice}
                                onChange={(e) => updateForm(idx, { subscriptionPrice: Number(e.target.value) })}
                                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900"
                                placeholder="Örn: 1200"
                                disabled={!form.subscriptionEnabled}
                              />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-sm text-gray-600 mb-2">Abonelik Kuralları</label>
                              <textarea
                                rows={3}
                                value={form.subscriptionRules}
                                onChange={(e) => updateForm(idx, { subscriptionRules: e.target.value })}
                                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 resize-none disabled:opacity-60"
                                placeholder="Örn: İptalde ücret tam iade edilir. Dekont açıklamasına takım adı yazın."
                                disabled={!form.subscriptionEnabled}
                              />
                            </div>
                          </div>
                        </section>

                        {/* Tesis Özellikleri */}
                        <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                          <h2 className="text-lg font-semibold text-gray-900 mb-2">Tesis Özellikleri</h2>
                          <p className="text-sm text-gray-500 mb-4">Her özellik için Evet veya Hayır seçin.</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {(Object.keys(AMENITY_LABELS) as (keyof Amenities)[]).map((key) => {
                              const item = AMENITY_LABELS[key];
                              const value = form.amenities[key];
                              return (
                                <div key={key} className="border border-gray-200 bg-gray-50 rounded-lg px-4 py-3 flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span>{item.icon}</span>
                                    <span className="text-sm font-medium text-gray-800">{item.label}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button type="button" onClick={() => setAmenity(idx, key, true)}
                                      className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${value ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-700 border-gray-300 hover:border-green-500'}`}>Evet</button>
                                    <button type="button" onClick={() => setAmenity(idx, key, false)}
                                      className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${!value ? 'bg-rose-600 text-white border-rose-600' : 'bg-white text-gray-700 border-gray-300 hover:border-rose-500'}`}>Hayır</button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </section>
                      </>
                    );
                  })()}

                  {error && (
                    <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3 text-sm">{error}</div>
                  )}

                  <div className="flex items-center justify-between gap-3">
                    <button type="button" onClick={() => navigate('/panel')}
                      className="px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors">Vazgeç</button>
                    <div className="flex items-center gap-3">
                      {forms.length > 1 && activeFieldIdx < forms.length - 1 && (
                        <button type="button" onClick={() => setActiveFieldIdx(activeFieldIdx + 1)}
                          className="px-5 py-2.5 rounded-lg border border-primary-300 text-primary-700 bg-primary-50 hover:bg-primary-100 transition-colors font-medium">Sonraki Saha</button>
                      )}
                      <button type="submit"
                        className="px-5 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-medium transition-colors">
                        {isEditMode ? 'Değişiklikleri Kaydet' : forms.length > 1 ? `${forms.length} Sahayı Kaydet` : 'Sahayı Kaydet'}
                      </button>
                    </div>
                  </div>
                </form>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AddFieldPage;
